import React, { useState, useRef } from "react";
import { Calendar, X, Upload } from "lucide-react";
import { Constants } from "../utils/constants";
import apiService from "../services/apiService";
import { apiV2Service, v2PublicApiService, organizationApiService } from "../services/apiV2Service";
import { getUser } from "../services/localService";
import { showApiMessage } from "../utils/messageHandler";
import { useOrganizationRoles } from "../hooks/useOrganizationRoles";
import logger from "../utils/logger";
import { spaceManagementService } from "../services/host-apiServices/spaceManagementService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Input } from "../shadcn/ui/input";
import { Textarea } from "../shadcn/ui/textarea";
import { Label } from "../shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../shadcn/ui/select";

const defaultAvatar =
  "https://storage.googleapis.com/maestro-8beff.appspot.com/user-543-_1626834155761_69ec79f5_1b40_4716_9958_1a6f557f6581.jpeg";

const HCreateAnnouncementModal = ({ isOpen, onClose, onSuccess }) => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [datetimeToSend, setDatetimeToSend] = useState(new Date(Date.now() + 3600000)); // 1 hour from now
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [isSearchingOrgs, setIsSearchingOrgs] = useState(false);
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userSearchType, setUserSearchType] = useState("staff"); // "staff" or "clients"
  const fileInputRef = useRef(null);

  const { organizationRoles, organizationRolesLoading, loadOrganizationRoles } = useOrganizationRoles();

  // Reset state when modal opens to ensure a fresh form
  React.useEffect(() => {
    if (isOpen) {
      setTitle("");
      setMessage("");
      setSelectedRoles([]);
      setSelectedUsers([]);
      setSelectedOrgs([]);
      setDatetimeToSend(new Date(Date.now() + 3600000));
      setUserSearchTerm("");
      setUserSearchResults([]);
      setOrgSearchTerm("");
      setOrgSearchResults([]);
      setShowRoleSelector(false);
      setShowUserSelector(false);
      setImageFile(null);
      setIsLoading(false);

      // Load roles
      setUserSearchType("staff");
      loadOrganizationRoles();
    }
  }, [isOpen, loadOrganizationRoles]);

  // Map organization roles to the internal format and filter by user org type
  const availableRoles = React.useMemo(() => {
    const currentUser = getUser();
    const org = currentUser?.user?.organization || currentUser?.organization;
    const userRoleCode =
      currentUser?.user?.organizationRole?.code || currentUser?.organizationRole?.code || currentUser?.role;

    // Robustly identify organization type ID (should be 2 or 3 for hosts)
    const typeIdFromOrg = org?.type?.id || org?.typeId;
    const typeIdFromUser = currentUser?.user?.organization?.typeId || currentUser?.user?.organization?.type?.id;
    const userOrgTypeId = typeIdFromOrg || typeIdFromUser;

    logger.log("🛠️ HCreateAnnouncementModal Role Filtering:", {
      userRoleCode,
      userOrgTypeId,
      totalRoles: organizationRoles?.length,
    });

    let roles = organizationRoles || [];

    // Filter roles based on user's organization type
    if (userRoleCode !== "ROLE_ADMINISTRATOR") {
      // Main org type (sub-accounts) roles are always allowed
      const allowedTypeIds = [];
      if (userOrgTypeId) {
        allowedTypeIds.push(userOrgTypeId);
      }

      // Host Property Manager (Type 3) can also target Host Corporate (6) and Host Individual (7)
      if (userOrgTypeId === 3 || String(userOrgTypeId) === "3") {
        allowedTypeIds.push(6, 7);
      }

      // Property Distribution Host (Type 2) can target Corporate (4) and Individual (5)
      if (userOrgTypeId === 2 || String(userOrgTypeId) === "2") {
        allowedTypeIds.push(4, 5);
      }

      logger.log("🛠️ Allowed Type IDs for current user:", allowedTypeIds);

      roles = roles.filter(role => {
        const isAllowed =
          allowedTypeIds.includes(role.organizationTypeId) || allowedTypeIds.includes(Number(role.organizationTypeId));
        return isAllowed;
      });
    }

    const formatted = roles.map(role => ({
      value: role.code,
      label: role.displayName || role.name,
      description: role.description,
      organizationTypeId: role.organizationTypeId,
    }));

    logger.log(
      "✅ Filtered roles to show:",
      formatted.map(r => r.label)
    );
    return formatted;
  }, [organizationRoles]);

  const fetchUsers = async term => {
    if (!term || term.length < 2) {
      setUserSearchResults([]);
      setShowUserSelector(false);
      return;
    }

    logger.log("🔍 fetchUsers called with term:", term);
    setIsSearchingUsers(true);
    setShowUserSelector(true);
    try {
      const currentUser = getUser();
      const organizationId =
        currentUser?.organizationId || currentUser?.user?.organizationId || currentUser?.organization?.id;

      logger.log("🔍 organizationId for user search:", organizationId);

      const params = { q: term, limit: 10, status: "ACTIVE" };

      if (userSearchType === "staff") {
        if (!organizationId) {
          logger.error("Organization ID not found for staff search.");
          setUserSearchResults([]);
          return;
        }
        params.organizationId = organizationId;
      } else {
        params.mode = "CLIENTS";
      }

      logger.log("🔍 searchUsers params:", params);
      const response = await apiV2Service.searchUsers(params);
      logger.log("📦 searchUsers response:", response);

      // Robustly handle different response formats
      const users = response?.data?.data || response?.data || [];
      logger.log("✅ searchUsers found users:", users);
      setUserSearchResults(Array.isArray(users) ? users : []);

      if (!Array.isArray(users) || users.length === 0) {
        logger.log("ℹ️ No users found for term:", term);
      }
    } catch (error) {
      logger.error("Error searching users:", error);
      setUserSearchResults([]);
    } finally {
      setIsSearchingUsers(false);
    }
  };

  const searchOrganizations = async term => {
    logger.log("🔍 searchOrganizations called with term:", term);
    if (!term || term.length < 2) {
      setOrgSearchResults([]);
      return;
    }

    setIsSearchingOrgs(true);
    try {
      const currentUser = getUser();
      const parentId =
        currentUser?.user?.organizationId || currentUser?.organizationId || currentUser?.organization?.id;

      logger.log("🔍 parentId for search:", parentId);

      if (!parentId) {
        logger.error("Parent ID not found for organization search.");
        setOrgSearchResults([]);
        return;
      }

      const response = await organizationApiService.searchOrganizations({
        q: term,
        parentId: parseInt(parentId),
        limit: 20,
      });
      logger.log("📦 organizationApiService response:", response);

      // Robustly handle different response formats
      const orgs = response?.data?.data || response?.data || [];
      logger.log("✅ searchOrganizations found orgs:", orgs);
      setOrgSearchResults(Array.isArray(orgs) ? orgs : []);

      if (!Array.isArray(orgs) || orgs.length === 0) {
        logger.log("ℹ️ No organizations found for term:", term);
      }
    } catch (error) {
      logger.error("Error searching organizations:", error);
      setOrgSearchResults([]);
    } finally {
      setIsSearchingOrgs(false);
    }
  };

  const toggleOrg = org => {
    setSelectedOrgs(prev => (prev.some(o => o.id === org.id) ? prev.filter(o => o.id !== org.id) : [...prev, org]));
  };

  const handleDateChange = e => {
    setDatetimeToSend(new Date(e.target.value));
  };

  const handleSave = async () => {
    if (isLoading) return;

    // Basic validation
    if (!title.trim() || !message.trim()) {
      showApiMessage(
        {
          response: {
            status: 400,
            data: { message: "Title and message are required" },
          },
        },
        "create announcement"
      );
      return;
    }

    try {
      setIsLoading(true);
      const iso = datetimeToSend instanceof Date ? datetimeToSend.toISOString() : new Date().toISOString();

      let imageUrl = null;
      if (imageFile) {
        const uploadResponse = await spaceManagementService.uploadMedia([imageFile]);
        if (
          (uploadResponse.status === 200 || uploadResponse.status === 201) &&
          uploadResponse.data &&
          uploadResponse.data.length > 0
        ) {
          imageUrl = uploadResponse.data[0];
        }
      }

      const payload = {
        title,
        message,
        roles: selectedRoles,
        userIds: selectedUsers.map(u => String(u.id)),
        organizationIds: selectedOrgs.map(o => o.id),
        imageUrls: imageUrl ? [imageUrl] : [],
        datetimeToSend: iso,
        priority: "LOW",
        hasCTA: false,
        actionType: "announcement",
        data: {},
      };

      const resp = await apiService.createAnnouncement(payload);
      showApiMessage(resp, "create announcement");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      showApiMessage(error, "create announcement");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRole = role => {
    setSelectedRoles(prev => (prev.includes(role.value) ? prev.filter(r => r !== role.value) : [...prev, role.value]));
  };

  const toggleUser = user => {
    setSelectedUsers(prev => (prev.some(u => u.id === user.id) ? prev.filter(u => u.id !== user.id) : [...prev, user]));
  };

  const formatDateTime = date => {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "Not scheduled";

    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  // No fixed user list; users are fetched based on search

  const handleImageUpload = e => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Create an Announcement</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1 space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Enter announcement title"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Write your announcement message..."
              className="mt-1 min-h-[80px]"
            />
          </div>

          {/* Image Upload */}
          <div>
            <Label>Image (Optional)</Label>
            {imageFile ? (
              <div className="relative mt-1">
                <img
                  src={URL.createObjectURL(imageFile)}
                  alt="Preview"
                  className="w-full h-28 object-cover rounded-lg"
                />
                <button
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current.click()}
                className="mt-1 border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50"
              >
                <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                <p className="text-sm text-gray-500">Click to upload an image</p>
                <p className="text-xs text-gray-400">PNG, JPG, GIF up to 5MB</p>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                />
              </div>
            )}
          </div>

          {/* Schedule Date and Time */}
          <div>
            <Label htmlFor="datetime">When to Send</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                id="datetime"
                type="datetime-local"
                value={
                  datetimeToSend instanceof Date && !isNaN(datetimeToSend.getTime())
                    ? datetimeToSend.toISOString().slice(0, 16)
                    : new Date().toISOString().slice(0, 16)
                }
                onChange={handleDateChange}
                className="flex-1"
              />
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
              >
                <Calendar className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Scheduled for: {formatDateTime(datetimeToSend)}</p>
          </div>

          {/* Target Audience Section - MOVED TO BOTTOM */}
          <div className="space-y-4 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Target Audience</h3>

            {/* Roles Section */}
            <div>
              <Label>Target Roles</Label>
              <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 min-h-[40px] mt-1">
                {selectedRoles.map(roleValue => {
                  const role = availableRoles.find(r => r.value === roleValue);
                  return (
                    <div
                      key={roleValue}
                      className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                    >
                      <span>{role?.label || roleValue}</span>
                      <button
                        className="w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center"
                        onClick={() => toggleRole({ value: roleValue })}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                {selectedRoles.length === 0 && <div className="text-gray-400 text-sm">No roles selected</div>}
              </div>
              <button
                className="text-sm font-medium mt-1 hover:underline"
                style={{ color: Constants.HOST_EXPERIENCE_BASE_COLOR }}
                onClick={() => setShowRoleSelector(!showRoleSelector)}
              >
                {showRoleSelector ? "Hide roles" : "Select roles"}
              </button>

              {/* Role Selector */}
              {showRoleSelector && (
                <div className="mt-1 p-2 bg-white border border-gray-200 rounded-lg max-h-[150px] overflow-y-auto">
                  {organizationRolesLoading ? (
                    <div className="p-2 text-center text-sm text-gray-500">Loading roles...</div>
                  ) : availableRoles.length > 0 ? (
                    availableRoles.map(role => (
                      <div
                        key={role.value}
                        className="flex items-center p-1.5 hover:bg-gray-50 cursor-pointer rounded"
                        onClick={() => toggleRole(role)}
                      >
                        <input type="checkbox" checked={selectedRoles.includes(role.value)} readOnly className="mr-2" />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{role.label}</span>
                          {role.description && <p className="text-xs text-gray-500">{role.description}</p>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-center text-sm text-gray-500">No roles available</div>
                  )}
                </div>
              )}
            </div>

            {/* Organization Selector */}
            <div>
              <Label>Target Specific Organizations (Optional)</Label>
              <div className="mt-1 relative">
                <Input
                  placeholder="Search sub-accounts or customers..."
                  value={orgSearchTerm}
                  onChange={e => {
                    const term = e.target.value;
                    setOrgSearchTerm(term);
                    searchOrganizations(term);
                  }}
                  className="pr-10"
                />
                {isSearchingOrgs && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                  </div>
                )}
              </div>

              {/* Selected Orgs Preview */}
              <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 min-h-[40px] max-h-[80px] overflow-y-auto mt-2">
                {selectedOrgs.map(org => (
                  <div
                    key={org.id}
                    className="flex items-center gap-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                  >
                    <span>{org.name}</span>
                    <button
                      className="w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center"
                      onClick={() => toggleOrg(org)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {selectedOrgs.length === 0 && <div className="text-gray-400 text-sm">No organizations selected</div>}
              </div>

              {/* Org Search Results */}
              {orgSearchTerm.length >= 2 && (
                <div className="mt-1 p-2 bg-white border border-gray-200 rounded-lg max-h-[150px] overflow-y-auto">
                  {isSearchingOrgs ? (
                    <div className="p-2 text-center text-sm text-gray-500">Searching organizations...</div>
                  ) : orgSearchResults.length > 0 ? (
                    orgSearchResults.map(org => (
                      <div
                        key={org.id}
                        className="flex items-center p-1.5 hover:bg-gray-50 cursor-pointer rounded"
                        onClick={() => toggleOrg(org)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedOrgs.some(o => o.id === org.id)}
                          readOnly
                          className="mr-2"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{org.name}</div>
                          <div className="text-xs text-gray-500 truncate">{org.code}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-center text-sm text-gray-500">No organizations found</div>
                  )}
                </div>
              )}
            </div>

            {/* Users Section */}
            <div>
              <Label>Target Users (Optional)</Label>

              {/* User Search Bar */}
              {/* User Search Type Toggle */}
              <div className="flex items-center gap-4 mb-2">
                <button
                  type="button"
                  onClick={() => {
                    setUserSearchType("staff");
                    setUserSearchTerm("");
                    setUserSearchResults([]);
                  }}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                    userSearchType === "staff" ? "bg-primary text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Staff Search
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUserSearchType("clients");
                    setUserSearchTerm("");
                    setUserSearchResults([]);
                  }}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                    userSearchType === "clients"
                      ? "bg-primary text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  Client Search
                </button>
              </div>

              <div className="mt-1 relative">
                <Input
                  placeholder={
                    userSearchType === "staff"
                      ? "Search staff by name or email..."
                      : "Search clients by name or email..."
                  }
                  value={userSearchTerm}
                  onChange={e => {
                    const term = e.target.value;
                    setUserSearchTerm(term);
                    fetchUsers(term);
                  }}
                />
                {isSearchingUsers && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                  </div>
                )}
              </div>

              {/* Selected Users Preview */}
              <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 min-h-[40px] max-h-[80px] overflow-y-auto mt-2">
                {selectedUsers.map(user => (
                  <div
                    key={user.id}
                    className="flex items-center gap-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs"
                  >
                    <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={user.imageUrl || defaultAvatar}
                        alt={user.firstName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span>
                      {user.firstName} {user.lastName || user.lastname}
                    </span>
                    <button
                      className="w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center"
                      onClick={() => toggleUser(user)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {selectedUsers.length === 0 && <div className="text-gray-400 text-sm">No users selected</div>}
              </div>

              {/* User Selector */}
              {showUserSelector && (
                <div className="mt-1 p-2 bg-white border border-gray-200 rounded-lg max-h-[150px] overflow-y-auto">
                  {isSearchingUsers ? (
                    <div className="p-2 text-center text-sm text-gray-500">Searching users...</div>
                  ) : userSearchResults.length > 0 ? (
                    userSearchResults.map(user => (
                      <div
                        key={user.id}
                        className="flex items-center p-1.5 hover:bg-gray-50 cursor-pointer rounded"
                        onClick={() => toggleUser(user)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedUsers.some(u => u.id === user.id)}
                          readOnly
                          className="mr-2"
                        />
                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 mr-2 bg-gray-100">
                          {user.imageUrl ? (
                            <img src={user.imageUrl} alt={user.firstName} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                              {user.firstName?.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {user.firstName} {user.lastName || user.lastname}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{user.emailAddress || user.username}</div>
                        </div>
                      </div>
                    ))
                  ) : userSearchTerm.length >= 2 ? (
                    <div className="p-2 text-center text-sm text-gray-500">No users found</div>
                  ) : (
                    <div className="p-2 text-center text-sm text-gray-500">Type at least 2 characters to search</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Send Out"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HCreateAnnouncementModal;
